/**
 * Post Utilities
 * 
 * Functions for processing post data and media
 */

/**
 * Extract common media properties from media object
 * @param {Object} media - Media object
 * @param {Object} post - Post object
 * @returns {Object} Common media properties
 */
function extractMediaProperties(media, post) {
  return {
    id: media.id,
    url: media.file?.url || media.mediaUrl || media.url,
    thumbnailUrl: media.file?.thumbnailUrl || media.thumbnailUrl || "",
    altText: media.altText,
    dimensions: media.dimensions,
    mediaType: media.mediaType,
    reactions: post.likesCount || post._count?.likes || 0,
    comments: post.commentsCount || post._count?.comments || 0,
    shares: post.sharesCount || post._count?.share || 0,
    createdAt: post.createdAt || post.created_at || post.timestamp || post.date || media.createdAt || media.created_at,
  };
}

/**
 * Process and sort media from a post
 * @param {Object} post - Post object
 * @param {Function} filterFn - Optional filter function for media
 * @param {Function} mapFn - Map function to transform media
 * @returns {Array} Processed media array
 */
function processPostMedia(post, filterFn = null, mapFn = null) {
  if (!post.media || post.media.length === 0) {
    return [];
  }

  let media = [...post.media].sort((a, b) => (a.uploadOrder ?? 0) - (b.uploadOrder ?? 0));

  if (filterFn) {
    media = media.filter(filterFn);
  }

  if (mapFn) {
    return media.map(mapFn);
  }

  return media.map(m => extractMediaProperties(m, post));
}

/**
 * Flatten photos from posts array
 * @param {Array} posts - Array of post objects
 * @returns {Array} Array of flattened photo objects
 */
export function flattenPhotosFromPosts(posts) {
  if (!Array.isArray(posts)) {
    return [];
  }

  return posts
    .filter((post) => post.media && post.media.length > 0)
    .flatMap((post) =>
      processPostMedia(
        post,
        (media) => media.mediaType === "IMAGE",
        (media) => extractMediaProperties(media, post),
      ),
    );
}

/**
 * Flatten media from posts array
 * @param {Array} posts - Array of post objects
 * @returns {Array} Array of flattened media objects
 */
export function flattenMediaFromPosts(posts) {
  if (!Array.isArray(posts)) {
    return [];
  }

  return posts
    .filter((post) => post.media && post.media.length > 0)
    .flatMap((post) =>
      processPostMedia(
        post,
        null,
        (media) => ({
          ...extractMediaProperties(media, post),
          post: post,
          author: post.user || post.author,
          description: post.content || post.description || "",
          postId: post.id,
        }),
      ),
    );
}

